import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import TrackerRoot from './TrackerRoot'
import Spinner from './components/Spinner'
import { fetchTrackerData } from './api'
import { useAtlasTracking, useOrderData, useOrderStatus, useTrackerData } from './hooks'
import { TrackerRuntimeProvider } from './runtime'
import {
  buildTimelineItems,
  buildTrackerPreviewData,
  formatEstimatedRange,
  formatEstimatedTime,
  getBufferedDropoffDate,
  normalizeApiBaseUrl,
} from './utils'
import {
  OrderDataInterface,
  TrackerEmbeddedProps,
  TrackerPreviewProps,
  TrackerStandaloneProps,
} from './types'

function useStableQueryClient() {
  const [queryClient] = React.useState(() => new QueryClient())
  return queryClient
}

interface TrackerProvidersProps {
  apiBaseUrl?: string
  atlasKey?: string
  children: React.ReactNode
  data?: OrderDataInterface
  mapTilesBaseUrl?: string
  orderId?: string
}

const TrackerProviders = ({
  apiBaseUrl,
  atlasKey,
  children,
  data,
  mapTilesBaseUrl,
  orderId,
}: TrackerProvidersProps) => {
  const queryClient = useStableQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <TrackerRuntimeProvider
        apiBaseUrl={apiBaseUrl}
        atlasKey={atlasKey}
        data={data}
        mapTilesBaseUrl={mapTilesBaseUrl}
        orderId={orderId}
      >
        {children}
      </TrackerRuntimeProvider>
    </QueryClientProvider>
  )
}

const DefaultLoading = () => {
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <h3 className="mb-5 text-2xl font-normal text-gray-700 text-opacity-80">
        Loading order...
      </h3>
      <div>
        <Spinner />
      </div>
    </div>
  )
}

const TrackerEmbedded = ({
  atlasKey,
  className,
  data,
  mapTilesBaseUrl,
  orderId,
}: TrackerEmbeddedProps) => {
  return (
    <TrackerProviders
      atlasKey={atlasKey}
      data={data}
      mapTilesBaseUrl={mapTilesBaseUrl}
      orderId={orderId}
    >
      <TrackerRoot className={className} orderId={orderId} />
    </TrackerProviders>
  )
}

const TrackerPreview = (props: TrackerPreviewProps) => {
  return <TrackerEmbedded {...props} />
}

const TrackerStandaloneInner = ({
  apiBaseUrl,
  atlasKey,
  className,
  mapTilesBaseUrl,
  errorFallback,
  loadingFallback,
  orderId,
}: TrackerStandaloneProps) => {
  const query = useTrackerData(orderId, apiBaseUrl)

  if (query.isLoading) {
    return <>{loadingFallback || <DefaultLoading />}</>
  }

  if (query.isError || !query.data) {
    return <>{errorFallback || null}</>
  }

  return (
    <TrackerRuntimeProvider
      apiBaseUrl={apiBaseUrl}
      atlasKey={atlasKey}
      data={query.data}
      mapTilesBaseUrl={mapTilesBaseUrl}
      orderId={orderId}
    >
      <TrackerRoot className={className} orderId={orderId} />
    </TrackerRuntimeProvider>
  )
}

const TrackerStandalone = ({
  apiBaseUrl,
  atlasKey,
  className,
  errorFallback,
  loadingFallback,
  mapTilesBaseUrl,
  orderId,
}: TrackerStandaloneProps) => {
  const queryClient = useStableQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <TrackerRuntimeProvider
        apiBaseUrl={apiBaseUrl}
        atlasKey={atlasKey}
        mapTilesBaseUrl={mapTilesBaseUrl}
        orderId={orderId}
      >
        <TrackerStandaloneInner
          apiBaseUrl={apiBaseUrl}
          atlasKey={atlasKey}
          className={className}
          errorFallback={errorFallback}
          loadingFallback={loadingFallback}
          mapTilesBaseUrl={mapTilesBaseUrl}
          orderId={orderId}
        />
      </TrackerRuntimeProvider>
    </QueryClientProvider>
  )
}

const Tracker = ({
  atlasKey,
  className,
  data,
  mapTilesBaseUrl,
  orderId,
}: TrackerEmbeddedProps) => {
  return (
    <TrackerEmbedded
      atlasKey={atlasKey}
      className={className}
      data={data}
      mapTilesBaseUrl={mapTilesBaseUrl}
      orderId={orderId}
    />
  )
}

export { fetchTrackerData }
export {
  fetchMapTileManifest,
  normalizeCountry,
  parseMapTileManifest,
  resolveMapTiles,
} from './mapTiles'
export { useAtlasTracking, useOrderData, useOrderStatus, useTrackerData }
export {
  buildTimelineItems,
  buildTrackerPreviewData,
  formatEstimatedRange,
  formatEstimatedTime,
  getBufferedDropoffDate,
  normalizeApiBaseUrl,
}
export * from './types'
export { Tracker, TrackerEmbedded, TrackerPreview, TrackerStandalone }
export { TrackerProviders }
export { TrackerRoot }
export { Tracker as default }
